/**
 * Receipt Information Service
 * Handles storage and retrieval of receipt information for charging sessions
 */

import axios from 'axios';

const RECEIPT_STORAGE_KEY = 'ev.receipt.info';
const RECEIPT_SENT_KEY = 'ev.receipt.sent';
const API_BASE = import.meta.env.VITE_API_URL;

/**
 * Store receipt information for a session
 * @param {string} sessionKey - Session or station identifier
 * @param {object} receiptInfo - Receipt information { fullName, email, chargerId, timestamp }
 */
export function storeReceiptInfo(sessionKey, receiptInfo) {
  try {
    const storage = getReceiptStorage();
    storage[sessionKey] = receiptInfo;
    localStorage.setItem(RECEIPT_STORAGE_KEY, JSON.stringify(storage));
    console.log(' Receipt info stored:', sessionKey, receiptInfo);
  } catch (error) {
    console.error('Error storing receipt info:', error);
  }
}

/**
 * Retrieve receipt information for a session
 * @param {string} sessionKey - Session or station identifier
 * @returns {object|null} Receipt information or null if not found
 */
export function getReceiptInfo(sessionKey) {
  try {
    const storage = getReceiptStorage();
    return storage[sessionKey] || null;
  } catch (error) {
    console.error('Error retrieving receipt info:', error);
    return null;
  }
}

/**
 * Clear receipt information for a session
 * @param {string} sessionKey - Session or station identifier
 */
export function clearReceiptInfo(sessionKey) {
  try {
    const storage = getReceiptStorage();
    delete storage[sessionKey];
    localStorage.setItem(RECEIPT_STORAGE_KEY, JSON.stringify(storage));
  } catch (error) {
    console.error('Error clearing receipt info:', error);
  }
}

/**
 * Get all receipt storage
 * @returns {object} Receipt storage object
 */
function getReceiptStorage() {
  try {
    const raw = localStorage.getItem(RECEIPT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.error('Error parsing receipt storage:', error);
    return {};
  }
}

/**
 * Clean up old receipt entries (older than 7 days)
 */
export function cleanupOldReceipts() {
  try {
    const storage = getReceiptStorage();
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    Object.keys(storage).forEach(key => {
      const receipt = storage[key];
      if (receipt?.timestamp) {
        const receiptTime = new Date(receipt.timestamp).getTime();
        if (receiptTime < sevenDaysAgo) {
          delete storage[key];
        }
      }
    });
    
    localStorage.setItem(RECEIPT_STORAGE_KEY, JSON.stringify(storage));
  } catch (error) {
    console.error('Error cleaning up old receipts:', error);
  }
}

/**
 * Check if receipt email has been sent for a session
 * @param {string} sessionId - Session ID
 * @returns {boolean} True if receipt has been sent
 */
export function hasReceiptBeenSent(sessionId) {
  try {
    const raw = localStorage.getItem(RECEIPT_SENT_KEY);
    const sentReceipts = raw ? JSON.parse(raw) : {};
    return !!sentReceipts[sessionId];
  } catch (error) {
    console.error('Error checking receipt sent status:', error);
    return false;
  }
}

/**
 * Mark receipt as sent for a session
 * @param {string} sessionId - Session ID
 */
export function markReceiptAsSent(sessionId) {
  try {
    const raw = localStorage.getItem(RECEIPT_SENT_KEY);
    const sentReceipts = raw ? JSON.parse(raw) : {};
    sentReceipts[sessionId] = Date.now();
    localStorage.setItem(RECEIPT_SENT_KEY, JSON.stringify(sentReceipts));
  } catch (error) {
    console.error('Error marking receipt as sent:', error);
  }
}

/**
 * Send receipt email via API
 * @param {object} params - Receipt parameters
 * @param {string} params.sessionId - Session ID
 * @param {string} params.chargerId - Charger/station ID
 * @param {string} params.fullName - Customer full name
 * @param {string} params.email - Customer email
 * @param {boolean} params.force - Force resend even if already sent (default: false)
 * @returns {Promise<object>} Response with success status
 */
export async function sendReceiptEmail({ sessionId, chargerId, fullName, email, force = false }) {
  // Check if receipt was already sent (only block if not forcing)
  const alreadySent = hasReceiptBeenSent(sessionId);
  
  if (alreadySent && !force) {
    console.log('📧 Receipt already sent for session:', sessionId, '- use force=true to resend');
    return {
      success: true,
      alreadySent: true,
      message: 'Receipt was already sent'
    };
  }

  try {
    const logPrefix = force ? '📧 [Resend]' : '📧 [Send]';
    
    // 🔍 COMPREHENSIVE DEBUG LOGGING
    console.log(`${logPrefix} ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    console.log(`${logPrefix} 🔍 RECEIPT SERVICE DEBUG:`);
    console.log(`${logPrefix}   sessionId: ${sessionId}`);
    console.log(`${logPrefix}   chargerId: ${chargerId}`);
    console.log(`${logPrefix}   email: ${email || 'null'}`);
    console.log(`${logPrefix}   fullName: ${fullName || 'null'}`);
    console.log(`${logPrefix}   force: ${force}`);
    console.log(`${logPrefix}   API_BASE: ${API_BASE}`);
    
    // Build URL with force query parameter if needed
    const url = force 
      ? `${API_BASE}/ev/sessions/${sessionId}/receipt/email?force=1`
      : `${API_BASE}/ev/sessions/${sessionId}/receipt/email`;
    
    console.log(`${logPrefix}   URL: ${url}`);
    
    const requestBody = {
      email,
      fullName
    };
    
    console.log(`${logPrefix}   Request body:`, requestBody);
    console.log(`${logPrefix} ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);

    const response = await axios.post(
      url,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    console.log(`${logPrefix} ✅ Receipt email sent successfully`);
    console.log(`${logPrefix} Response status: ${response.status}`);
    console.log(`${logPrefix} Response data:`, response.data);
    
    // Mark as sent in local storage
    markReceiptAsSent(sessionId);

    return {
      success: true,
      alreadySent: alreadySent,
      resent: force && alreadySent,
      ...response.data
    };
  } catch (error) {
    console.error('📧 ✗ Failed to send receipt email');
    console.error('📧 Error response:', error.response?.data);
    console.error('📧 Error message:', error.message);
    console.error('📧 Error status:', error.response?.status);
    
    // Extract detailed error information
    const errorMessage = error.response?.data?.message || error.message || 'Failed to send receipt';
    const errorDetails = error.response?.data?.details;
    const correlationId = error.response?.data?.correlationId;
    
    console.error('📧 Parsed error:', {
      message: errorMessage,
      details: errorDetails,
      correlationId
    });

    return {
      success: false,
      error: errorMessage,
      details: errorDetails,
      correlationId
    };
  }
}
